import { useMemo } from "react";
import { COMPETENCY_MAPPING_TYPES, formatTypeLabel } from "../constants/interviewTypes";

const useInterviewData = (data, genState = {}, configuredTypes = []) => {
  return useMemo(() => {
    const types = {};
    let totalQuestions = 0;

    const activeKeys = new Set(configuredTypes.map((ct) => ct.value));

    const allDataKeys = new Set([...Object.keys(data || {}), ...Object.keys(genState || {})]);
    const inactiveDataKeys = Array.from(allDataKeys).filter((k) => {
      if (k === "id" || activeKeys.has(k)) return false;
      return data?.[k]?.questions_data || genState?.[k]?.regenProgress;
    });

    const TYPE_ORDER = [
      "EXPERIENCE_BASED",
      "SCREENING",
      "DOMAIN_SPECIFIC",
      "SITUATIONAL_CASE",
      "MOTIVATIONAL",
      "LEADERSHIP",
    ];

    const allTypeEntries = [
      ...configuredTypes.map((ct) => ({ value: ct.value, label: ct.label, isActive: true })),
      ...inactiveDataKeys.map((k) => ({ value: k, label: formatTypeLabel(k), isActive: false })),
    ].sort((a, b) => {
      const ai = TYPE_ORDER.indexOf(a.value);
      const bi = TYPE_ORDER.indexOf(b.value);
      const aIdx = ai === -1 ? Infinity : ai;
      const bIdx = bi === -1 ? Infinity : bi;
      return aIdx - bIdx;
    });

    for (const entry of allTypeEntries) {
      const typeKey = entry.value;
      const record = data?.[typeKey];
      const qd = record?.questions_data ?? {};
      const liveData = genState?.[typeKey]?.liveData;

      const genEntry = genState?.[typeKey];
      const regenProgress = genEntry?.regenProgress ?? null;
      const typeEntry = {
        questions: regenProgress ? [] : (liveData?.questions ?? (Array.isArray(qd.questions) ? qd.questions : [])),
        status: record?.status ?? qd.status,
        regenProgress,
        isActive: entry.isActive,
      };
      typeEntry.hasQuestions = typeEntry.questions.length > 0;
      totalQuestions += typeEntry.questions.length;

      if (COMPETENCY_MAPPING_TYPES.includes(typeKey)) {
        typeEntry.competencies = regenProgress ? [] : (liveData?.competencies ?? (Array.isArray(qd.competencies) ? qd.competencies : []));
        typeEntry.mappings = regenProgress ? [] : (liveData?.mappings ?? (Array.isArray(qd.mappings) ? qd.mappings : []));
      }

      types[typeKey] = typeEntry;
    }

    return {
      types,
      counts: { questions: totalQuestions },
      allTypeEntries,
    };
  }, [data, genState, configuredTypes]);
};

export default useInterviewData;
